/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Chip,
  Tabs,
  Tab,
  Tooltip,
} from '@material-ui/core';
import {
  Close,
  Public,
  Lock,
  Group,
  Category,
  Add,
  VideoLibrary,
  PhotoLibrary,
  ExpandMore,
  ExpandLess,
  ArrowBack,
  CheckCircle,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import { endpoints, dataFetcher } from 'Api';
import AlterImage from 'Assets/no-image-available.png';
import CreateCategoryPage from 'Pages/private/content-layout/CreateCategory';

const PublishSettingsModal = ({
  open,
  onClose,
  onPublish,
  selectedItems = [],
  isPublishing = false,
}) => {
  // Mode: 'simple' or 'advanced'
  const [mode, setMode] = useState('simple');
  const [activeTab, setActiveTab] = useState(0);

  // Separate selected items by type
  const videoItems = selectedItems.filter((item) => item.type === 'video');
  const albumItems = selectedItems.filter((item) => item.type === 'album');

  const hasVideos = videoItems.length > 0;
  const hasAlbums = albumItems.length > 0;

  // Get default values from first video item if exists
  const defaultVideoPayload = hasVideos ? videoItems[0]?.payload?.payload : null;
  const defaultAlbumPayload = hasAlbums ? albumItems[0]?.payload?.payload : null;

  // Video settings state (Simple mode)
  const [videoCategory, setVideoCategory] = useState({
    type: 'name',
    value: '',
    name: '',
  });
  const [videoVisibility, setVideoVisibility] = useState('1');
  const [videoSelectedGroups, setVideoSelectedGroups] = useState([]);

  // Album settings state (Simple mode)
  const [albumVisibility, setAlbumVisibility] = useState('2');
  const [albumSelectedGroups, setAlbumSelectedGroups] = useState([]);

  // Advanced mode state
  const [editedItems, setEditedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  // Categories list
  const [categories, setCategories] = useState([]);
  const [gettingCategories, setGettingCategories] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [pendingItemIdForCategory, setPendingItemIdForCategory] = useState(null);

  // Toolbar dialog states
  const [applyCategoryDialogOpen, setApplyCategoryDialogOpen] = useState(false);
  const [selectedCategoryForAll, setSelectedCategoryForAll] = useState('');
  const [applyVisibilityDialogOpen, setApplyVisibilityDialogOpen] = useState(false);
  const [selectedVisibilityForAll, setSelectedVisibilityForAll] = useState('2');

  // Groups list
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Fetch categories when modal opens
  const fetchCategories = async () => {
    setGettingCategories(true);
    try {
      // getMyCategory (singular) returns the category background image URL in
      // `icon` (getMyCategories only returns the icon id), so we use it here to
      // render category backgrounds in the dropdown.
      const response = await dataFetcher(endpoints.getMyCategory);
      if (response.success && response.data) {
        const sortedCategories = [...response.data].sort((a, b) => {
          const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
          const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
          if (aIsAllVideos && !bIsAllVideos) return -1;
          if (!aIsAllVideos && bIsAllVideos) return 1;
          return 0;
        });
        setCategories(sortedCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setGettingCategories(false);
    }
  };

  // Fetch groups when modal opens
  const fetchGroups = async () => {
    setGroupsLoaded(false);
    try {
      const response = await dataFetcher(endpoints.getMyGroups);
      if (response.success && response.data) {
        setGroups(response.data);
      } else if (!response.success && response.message === 'No Groups found') {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setGroupsLoaded(true);
    }
  };

  // Initialize edited items for advanced mode
  const initializeEditedItems = (categoriesList = []) => {
    // Find "All Videos" category
    const allVideosCategory = categoriesList.find(
      (cat) => cat.title?.toLowerCase() === 'all videos'
    );

    const items = selectedItems.map((contrib) => {
      const payload = contrib.payload?.payload || {};
      const type = contrib.type || (payload.photos ? 'album' : 'video');

      // For videos, auto-select "All Videos" if no category is set
      let selectedCategory;
      if (type === 'video') {
        if (payload.category_id) {
          selectedCategory = {
            type: 'id',
            value: payload.category_id,
            name: payload.category_name || '',
          };
        } else if (allVideosCategory) {
          // Auto-select "All Videos" category
          selectedCategory = {
            type: 'id',
            value: allVideosCategory.id,
            name: allVideosCategory.title,
          };
        } else {
          selectedCategory = {
            type: 'name',
            value: '',
            name: payload.category_name || '',
          };
        }
      } else {
        // Albums don't have categories
        selectedCategory = {
          type: 'name',
          value: '',
          name: '',
        };
      }

      // Parse thumbnails for videos
      let thumbnails = [];
      if (type === 'video' && payload.thumbnails_signed) {
        thumbnails = Array.isArray(payload.thumbnails_signed)
          ? payload.thumbnails_signed
          : [];
      }
      // If no thumbnails array, use single thumbnail as fallback
      const mainThumbnail = payload.thumbnail_signed || payload.icon_signed || null;
      if (thumbnails.length === 0 && mainThumbnail) {
        thumbnails = [mainThumbnail];
      }
      // Find selected thumbnail index
      const selectedThumbnailIndex = mainThumbnail
        ? Math.max(0, thumbnails.findIndex((t) => t === mainThumbnail))
        : 0;

      return {
        id: contrib.id,
        uuid: contrib.uuid,
        uniqueKey: contrib.id || contrib.uuid,
        type,
        title: payload.title || '',
        description: payload.description || '',
        visibility: payload.visibility || '2',
        selectedGroups: [],
        category_id: payload.category_id || '',
        category_name: payload.category_name || '',
        selectedCategory,
        thumbnail: mainThumbnail,
        thumbnails,
        selectedThumbnail: selectedThumbnailIndex,
        originalContrib: contrib,
      };
    });
    setEditedItems(items);
    setExpandedItems({});
  };

  // Initialize defaults and fetch data when modal opens
  useEffect(() => {
    if (open) {
      fetchCategories();
      fetchGroups();
      setMode('simple');
      setActiveTab(0);

      setVideoSelectedGroups([]);
      setAlbumSelectedGroups([]);

      if (defaultVideoPayload) {
        setVideoCategory({
          type: 'name',
          value: '',
          name: defaultVideoPayload.category_name || '',
        });
        setVideoVisibility(defaultVideoPayload.visibility || '1');
      }

      if (defaultAlbumPayload) {
        setAlbumVisibility(defaultAlbumPayload.visibility || '2');
      }

      initializeEditedItems([]);
    }
  }, [open, selectedItems]);

  // Re-initialize edited items when categories are loaded to auto-select "All Videos"
  useEffect(() => {
    if (open && categories.length > 0 && !gettingCategories) {
      initializeEditedItems(categories);
    }
  }, [categories, gettingCategories]);

  const handleCategoryChange = (e) => {
    const selectedValue = e.target.value;

    if (selectedValue === '__create_new__') {
      setPendingItemIdForCategory('__simple__');
      setShowCreateCategoryModal(true);
      return;
    }

    if (selectedValue === '__contributor_category__') {
      setVideoCategory({
        type: 'name',
        value: '',
        name: defaultVideoPayload?.category_name || '',
      });
      return;
    }

    const selectedCat = categories.find((cat) => cat.id === selectedValue);
    setVideoCategory({
      type: 'id',
      value: selectedValue,
      name: selectedCat?.title || '',
    });
  };

  const handlePublish = () => {
    if (mode === 'advanced') {
      // Validate group selection for items with Group visibility
      const invalidItems = editedItems.filter(
        (item) => item.visibility === '3'
          && (!item.selectedGroups || item.selectedGroups.length === 0)
      );

      if (invalidItems.length > 0) {
        const expandedState = {};
        invalidItems.forEach((item) => {
          expandedState[item.uniqueKey] = true;
        });
        setExpandedItems((prev) => ({ ...prev, ...expandedState }));

        const firstInvalid = invalidItems[0];
        if (firstInvalid.type === 'video') {
          setActiveTab(0);
        } else {
          setActiveTab(1);
        }
        return;
      }

      const settings = {
        useAdvanced: true,
        items: editedItems,
        video: hasVideos
          ? {
            category_id: videoCategory.type === 'id' ? videoCategory.value : '',
            category_name: videoCategory.type === 'name' ? videoCategory.name : '',
            visibility: videoVisibility,
            group_ids: videoVisibility === '3' ? videoSelectedGroups : [],
          }
          : null,
        album: hasAlbums
          ? {
            visibility: albumVisibility,
            group_ids: albumVisibility === '3' ? albumSelectedGroups : [],
          }
          : null,
      };
      onPublish(settings);
      return;
    }

    // Simple mode validation
    if (videoVisibility === '3' && hasVideos && videoSelectedGroups.length === 0) {
      return;
    }
    if (albumVisibility === '3' && hasAlbums && albumSelectedGroups.length === 0) {
      return;
    }

    const settings = {
      video: hasVideos
        ? {
          category_id: videoCategory.type === 'id' ? videoCategory.value : '',
          category_name: videoCategory.type === 'name' ? videoCategory.name : '',
          visibility: videoVisibility,
          group_ids: videoVisibility === '3' ? videoSelectedGroups : [],
        }
        : null,
      album: hasAlbums
        ? {
          visibility: albumVisibility,
          group_ids: albumVisibility === '3' ? albumSelectedGroups : [],
        }
        : null,
    };
    onPublish(settings);
  };

  // Advanced mode functions - defined before handleCategoryCreated which uses updateItem
  const updateItem = (itemId, updates) => {
    setEditedItems((prev) => prev.map((item) => (item.uniqueKey === itemId ? { ...item, ...updates } : item)));

    if (updates.visibility === '3') {
      const item = editedItems.find((i) => i.uniqueKey === itemId);
      if (!item?.selectedGroups || item.selectedGroups.length === 0) {
        setExpandedItems((prev) => ({ ...prev, [itemId]: true }));
      }
    }
  };

  // Toolbar handlers for quick actions
  const handleClearAllTitles = (type) => {
    setEditedItems((prev) => prev.map((item) => (item.type === type ? { ...item, title: '' } : item)));
  };

  const handleClearAllDescriptions = (type) => {
    setEditedItems((prev) => prev.map((item) => (item.type === type ? { ...item, description: '' } : item)));
  };

  const handleApplyCategoryToAll = () => {
    if (selectedCategoryForAll) {
      const selectedCat = categories.find((cat) => cat.id === selectedCategoryForAll);
      if (selectedCat) {
        const currentType = activeTab === 0 ? 'video' : 'album';
        setEditedItems((prev) => prev.map((item) => {
          if (item.type === currentType) {
            return {
              ...item,
              selectedCategory: {
                type: 'id',
                value: selectedCat.id,
                name: selectedCat.title,
              },
            };
          }
          return item;
        }));
        setApplyCategoryDialogOpen(false);
        setSelectedCategoryForAll('');
      }
    }
  };

  const handleApplyVisibilityToAll = () => {
    const currentType = activeTab === 0 ? 'video' : 'album';
    setEditedItems((prev) => prev.map((item) => {
      if (item.type === currentType) {
        return {
          ...item,
          visibility: selectedVisibilityForAll,
          selectedGroups: selectedVisibilityForAll === '3' ? item.selectedGroups : [],
        };
      }
      return item;
    }));
    // If setting to group visibility, expand all items of this type to show group selection
    if (selectedVisibilityForAll === '3') {
      const expandedState = {};
      editedItems.forEach((item) => {
        if (item.type === currentType) {
          expandedState[item.uniqueKey] = true;
        }
      });
      setExpandedItems((prev) => ({ ...prev, ...expandedState }));
    }
    setApplyVisibilityDialogOpen(false);
  };

  const handleResetAll = () => {
    // Re-initialize items from original contributions
    initializeEditedItems(categories);
  };

  const handleCategoryCreated = async () => {
    try {
      const response = await dataFetcher(endpoints.getMyCategory);
      if (response.success && response.data) {
        const sortedCategoriesForDisplay = [...response.data].sort((a, b) => {
          const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
          const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
          if (aIsAllVideos && !bIsAllVideos) return -1;
          if (!aIsAllVideos && bIsAllVideos) return 1;
          return 0;
        });
        setCategories(sortedCategoriesForDisplay);

        const sortedById = [...response.data].sort((a, b) => b.id - a.id);
        if (sortedById.length > 0) {
          const newestCategory = sortedById[0];

          if (pendingItemIdForCategory === '__simple__') {
            setVideoCategory({
              type: 'id',
              value: newestCategory.id,
              name: newestCategory.title,
            });
          } else if (pendingItemIdForCategory) {
            updateItem(pendingItemIdForCategory, {
              selectedCategory: {
                type: 'id',
                value: newestCategory.id,
                name: newestCategory.title,
              },
            });
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing categories:', error);
    } finally {
      setPendingItemIdForCategory(null);
      setShowCreateCategoryModal(false);
    }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case '1':
        return <Lock style={{ fontSize: 16, color: '#EF4444' }} />;
      case '3':
        return <Group style={{ fontSize: 16, color: '#5aa5ef' }} />;
      default:
        return <Public style={{ fontSize: 16, color: '#10B981' }} />;
    }
  };

  const getVisibilityText = (visibility) => {
    switch (visibility) {
      case '1':
        return 'Private';
      case '3':
        return 'Group';
      default:
        return 'All Viewers';
    }
  };

  // Small category-background thumbnail (icon = signed background image URL from
  // getMyCategory). Falls back to the placeholder when missing/broken.
  const renderCategoryThumb = (iconUrl) => (
    <div
      style={{
        width: 28,
        height: 20,
        borderRadius: 4,
        overflow: 'hidden',
        flexShrink: 0,
        marginRight: 8,
        backgroundColor: '#f3f4f6',
      }}
    >
      <img
        src={iconUrl || AlterImage}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => { e.target.src = AlterImage; }}
      />
    </div>
  );

  // Styles
  const labelStyle = {
    display: 'block',
    marginBottom: 8,
    fontSize: 13,
    fontWeight: 700,
    color: '#1f2937',
    fontFamily: 'Montserrat',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const selectStyle = {
    backgroundColor: '#fff',
    borderRadius: 10,
    fontFamily: 'Montserrat',
    fontSize: 13,
  };

  const sectionTitleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid #e5e7eb',
  };

  // Separate videos and albums for advanced mode
  const videos = editedItems.filter((item) => item.type === 'video');
  const albums = editedItems.filter((item) => item.type === 'album');

  const renderAdvancedItemRow = (item, isVideo) => (
    <div
      key={item.uniqueKey}
      style={{
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 12,
        border: item.visibility === '3' && (!item.selectedGroups || item.selectedGroups.length === 0)
          ? '2px solid #FCA5A5'
          : '1px solid #e5e7eb',
        overflow: 'hidden',
      }}
    >
      {/* Main Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          gap: 12,
        }}
      >
        {/* Thumbnail */}
        <div
          style={{
            width: 70,
            height: 45,
            borderRadius: 8,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: '#f3f4f6',
          }}
        >
          <img
            src={item.thumbnail || AlterImage}
            alt={item.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.src = AlterImage; }}
          />
        </div>

        {/* Title */}
        <div style={{ flex: 1, minWidth: 120 }}>
          <TextField
            value={item.title}
            onChange={(e) => updateItem(item.uniqueKey, { title: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Enter title..."
            InputProps={{
              style: {
                fontFamily: 'Montserrat',
                fontSize: 12,
                borderRadius: 8,
              },
            }}
          />
        </div>

        {/* Category (Videos only) */}
        {isVideo && (
          <div style={{ width: 140, flexShrink: 0 }}>
            {gettingCategories ? (
              <CircularProgress size={18} style={{ color: '#5aa5ef' }} />
            ) : (
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={item.selectedCategory.value}
                  onChange={(e) => {
                    if (e.target.value === '__create_new__') {
                      setPendingItemIdForCategory(item.uniqueKey);
                      setShowCreateCategoryModal(true);
                      return;
                    }
                    const selectedCat = categories.find((cat) => cat.id === e.target.value);
                    updateItem(item.uniqueKey, {
                      selectedCategory: {
                        type: 'id',
                        value: e.target.value,
                        name: selectedCat?.title || '',
                      },
                    });
                  }}
                  style={{
                    fontFamily: 'Montserrat',
                    fontSize: 12,
                    borderRadius: 8,
                  }}
                  renderValue={(value) => {
                    const cat = categories.find((c) => c.id === value);
                    const name = cat?.title || item.selectedCategory.name || 'Select';
                    return (
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          maxWidth: '100px',
                        }}
                      >
                        {name}
                      </span>
                    );
                  }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 300 } },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem value="__create_new__">
                    <div style={{ display: 'flex', alignItems: 'center', color: '#5aa5ef' }}>
                      <Add style={{ fontSize: 14, marginRight: 6 }} />
                      <span style={{ fontWeight: 600, fontSize: 11 }}>Create New</span>
                    </div>
                  </MenuItem>
                  <Divider style={{ margin: '4px 0' }} />
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          maxWidth: 200,
                          overflow: 'hidden',
                        }}
                      >
                        <Category style={{ fontSize: 14, marginRight: 6, color: '#6B7280', flexShrink: 0 }} />
                        <span
                          style={{
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cat.title}
                        </span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        )}

        {/* Visibility */}
        <div
          style={{
            width: 100,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Chip
            icon={getVisibilityIcon(item.visibility)}
            label={getVisibilityText(item.visibility)}
            onClick={() => {
              const visibilities = ['2', '1', '3'];
              const currentIndex = visibilities.indexOf(item.visibility);
              const nextIndex = (currentIndex + 1) % visibilities.length;
              updateItem(item.uniqueKey, { visibility: visibilities[nextIndex] });
            }}
            clickable
            size="small"
            style={{
              fontFamily: 'Montserrat',
              fontSize: 10,
              fontWeight: 500,
              backgroundColor: item.visibility === '1' ? '#FEE2E2'
                : item.visibility === '3' ? '#EDE9FE' : '#D1FAE5',
            }}
          />
        </div>

        {/* Expand button */}
        <IconButton
          size="small"
          onClick={() => toggleExpanded(item.uniqueKey)}
          style={{ color: '#6B7280', flexShrink: 0 }}
        >
          {expandedItems[item.uniqueKey] ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>

      {/* Expanded Content */}
      {expandedItems[item.uniqueKey] && (
        <div
          style={{
            padding: '0 16px 16px 16px',
            borderTop: '1px solid #f3f4f6',
            backgroundColor: '#fafafa',
          }}
        >
          <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Thumbnail Selection (only for videos with multiple thumbnails) */}
            {item.type === 'video' && item.thumbnails && item.thumbnails.length > 1 && (
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 6,
                    fontFamily: 'Montserrat',
                  }}
                >
                  Select Thumbnail
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 8,
                  }}
                >
                  {item.thumbnails.map((thumb, index) => (
                    <div
                      key={index}
                      onClick={() => updateItem(item.uniqueKey, {
                        selectedThumbnail: index,
                        thumbnail: thumb,
                      })}
                      style={{
                        position: 'relative',
                        aspectRatio: '16/9',
                        borderRadius: 6,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: item.selectedThumbnail === index
                          ? '2px solid #5aa5ef'
                          : '2px solid transparent',
                        boxShadow: item.selectedThumbnail === index
                          ? '0 0 0 3px rgba(90, 165, 239, 0.2)'
                          : 'none',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <img
                        src={thumb}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.src = AlterImage;
                        }}
                      />
                      {item.selectedThumbnail === index && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#5aa5ef',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle style={{ fontSize: 16, color: 'white' }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 6,
                  fontFamily: 'Montserrat',
                }}
              >
                Description
              </label>
              <TextField
                value={item.description}
                onChange={(e) => updateItem(item.uniqueKey, { description: e.target.value })}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Add description..."
                InputProps={{
                  style: {
                    fontFamily: 'Montserrat',
                    fontSize: 12,
                    borderRadius: 8,
                  },
                }}
              />
            </div>

            {/* Group Selection (when visibility is Group) */}
            {item.visibility === '3' && (
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 6,
                    fontFamily: 'Montserrat',
                  }}
                >
                  Select Groups
                </label>
                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 10,
                    padding: '8px',
                    maxHeight: '100px',
                    overflowY: 'auto',
                    border: item.selectedGroups.length === 0
                      ? '2px solid #EF4444'
                      : '1px solid #e5e7eb',
                  }}
                >
                  {!groupsLoaded ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 36 }}>
                      <CircularProgress size={14} style={{ color: '#5aa5ef' }} />
                      <span style={{ marginLeft: 8, color: '#6B7280', fontSize: 11 }}>Loading...</span>
                    </div>
                  ) : groups.length === 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 36,
                        backgroundColor: '#FEF2F2',
                        borderRadius: 8,
                      }}
                    >
                      <span style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: 11 }}>
                        No groups available
                      </span>
                    </div>
                  ) : (
                    groups.map((group) => (
                      <FormControlLabel
                        key={group.id}
                        control={(
                          <Checkbox
                            checked={item.selectedGroups.includes(group.id)}
                            onChange={(e) => {
                              const newGroups = e.target.checked
                                ? [...item.selectedGroups, group.id]
                                : item.selectedGroups.filter((id) => id !== group.id);
                              updateItem(item.uniqueKey, { selectedGroups: newGroups });
                            }}
                            style={{ color: '#5aa5ef', padding: 4 }}
                            size="small"
                          />
                        )}
                        label={(
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Group style={{ fontSize: 12, marginRight: 4, color: '#5aa5ef' }} />
                            <span style={{ fontFamily: 'Montserrat', fontSize: 11 }}>{group.title}</span>
                          </div>
                        )}
                        style={{ margin: 0, padding: '2px 4px', width: '100%' }}
                      />
                    ))
                  )}
                </div>
                {item.selectedGroups.length === 0 && groupsLoaded && groups.length > 0 && (
                  <span style={{ color: '#EF4444', fontSize: 10, marginTop: 4, display: 'block' }}>
                    Please select at least one group
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Simple Mode Dialog */}
      <Dialog
        open={open && mode === 'simple'}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            background: '#ffffff',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: '#1a1a1a',
              fontFamily: 'Montserrat',
            }}
          >
            Publish Settings
          </h2>
          <IconButton
            onClick={onClose}
            size="small"
            style={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </div>

        {/* Content */}
        <DialogContent
          style={{
            padding: '24px',
            backgroundColor: '#fafafa',
          }}
        >
          {/* Summary */}
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              border: '1px solid #e5e7eb',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: '#6B7280',
                fontFamily: 'Montserrat',
              }}
            >
              {'Publishing '}
              <strong style={{ color: '#1f2937' }}>
                {`${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''}`}
              </strong>
              {hasVideos && hasAlbums && (
                <span>
                  {` (${videoItems.length} video${videoItems.length !== 1 ? 's' : ''}, ${albumItems.length} album${albumItems.length !== 1 ? 's' : ''})`}
                </span>
              )}
            </p>
          </div>

          {/* Video Settings Section */}
          {hasVideos && (
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: 20,
                marginBottom: hasAlbums ? 20 : 0,
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={sectionTitleStyle}>
                <VideoLibrary style={{ fontSize: 20, color: '#5aa5ef' }} />
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#1f2937',
                    fontFamily: 'Montserrat',
                  }}
                >
                  Video Settings
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: '#9CA3AF',
                    fontFamily: 'Montserrat',
                    marginLeft: 'auto',
                  }}
                >
                  {`${videoItems.length} video${videoItems.length !== 1 ? 's' : ''}`}
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 16,
                }}
              >
                {/* Category Dropdown */}
                <div>
                  <label style={labelStyle}>Category</label>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Select
                      value={
                        videoCategory.type === 'name'
                          ? '__contributor_category__'
                          : videoCategory.value
                      }
                      onChange={handleCategoryChange}
                      style={selectStyle}
                      renderValue={(value) => {
                        if (value === '__contributor_category__') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Category style={{ fontSize: 16, marginRight: 8, color: '#F59E0B' }} />
                              <span style={{ fontSize: 13, fontFamily: 'Montserrat' }}>
                                {videoCategory.name || defaultVideoPayload?.category_name || 'Select'}
                              </span>
                            </div>
                          );
                        }
                        const cat = categories.find((c) => c.id === value);
                        return (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {renderCategoryThumb(cat?.icon)}
                            <span
                              style={{
                                fontSize: 13,
                                fontFamily: 'Montserrat',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {cat?.title || videoCategory.name || 'Select'}
                            </span>
                          </div>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        getContentAnchorEl: null,
                      }}
                    >
                      <MenuItem value="__create_new__">
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#5aa5ef',
                          }}
                        >
                          <Add
                            style={{
                              fontSize: 16,
                              marginRight: 8,
                              color: '#5aa5ef',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 13,
                              fontFamily: 'Montserrat',
                              fontWeight: 600,
                            }}
                          >
                            Create New Category
                          </span>
                        </div>
                      </MenuItem>
                      <Divider style={{ margin: '4px 0' }} />

                      {defaultVideoPayload?.category_name && (
                        <MenuItem value="__contributor_category__">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Category
                              style={{
                                fontSize: 16,
                                marginRight: 8,
                                color: '#F59E0B',
                              }}
                            />
                            <span
                              style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                            >
                              {defaultVideoPayload.category_name}
                              <span
                                style={{
                                  color: '#9CA3AF',
                                  fontSize: 11,
                                  marginLeft: 6,
                                }}
                              >
                                (Contributor&apos;s)
                              </span>
                            </span>
                          </div>
                        </MenuItem>
                      )}

                      {gettingCategories ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} style={{ marginRight: 8 }} />
                          Loading categories...
                        </MenuItem>
                      ) : (
                        categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {renderCategoryThumb(cat.icon)}
                              <span
                                style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                              >
                                {cat.title}
                              </span>
                            </div>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </div>

                {/* Visibility Dropdown */}
                <div>
                  <label style={labelStyle}>Visibility</label>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Select
                      value={videoVisibility}
                      onChange={(e) => setVideoVisibility(e.target.value)}
                      style={selectStyle}
                    >
                      <MenuItem value="2">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Public
                            style={{
                              fontSize: 16,
                              marginRight: 8,
                              color: '#10B981',
                            }}
                          />
                          <span
                            style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                          >
                            All Viewers
                          </span>
                        </div>
                      </MenuItem>
                      <MenuItem value="1">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Lock
                            style={{
                              fontSize: 16,
                              marginRight: 8,
                              color: '#EF4444',
                            }}
                          />
                          <span
                            style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                          >
                            Private
                          </span>
                        </div>
                      </MenuItem>
                      <MenuItem value="3">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Group
                            style={{
                              fontSize: 16,
                              marginRight: 8,
                              color: '#5aa5ef',
                            }}
                          />
                          <span
                            style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                          >
                            Group
                          </span>
                        </div>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </div>

              {/* Group Selection (when visibility is Group) */}
              {videoVisibility === '3' && (
                <div style={{ marginTop: 16 }}>
                  <label style={labelStyle}>Select Groups</label>
                  <div
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: 10,
                      padding: '8px',
                      maxHeight: '140px',
                      overflowY: 'auto',
                      border: videoSelectedGroups.length === 0
                        ? '2px solid #EF4444'
                        : '2px solid #e5e7eb',
                      boxShadow: videoSelectedGroups.length === 0
                        ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                        : 'none',
                    }}
                  >
                    {!groupsLoaded ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '50px',
                        }}
                      >
                        <CircularProgress size={16} style={{ color: '#5aa5ef' }} />
                        <span
                          style={{
                            marginLeft: '8px',
                            color: '#6B7280',
                            fontSize: '13px',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          Loading groups...
                        </span>
                      </div>
                    ) : groups.length === 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '50px',
                          backgroundColor: '#FEF2F2',
                          borderRadius: '8px',
                        }}
                      >
                        <span
                          style={{
                            color: '#9CA3AF',
                            fontStyle: 'italic',
                            fontSize: '13px',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          No groups available
                        </span>
                      </div>
                    ) : (
                      groups.map((group) => (
                        <FormControlLabel
                          key={group.id}
                          control={(
                            <Checkbox
                              checked={videoSelectedGroups.includes(group.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setVideoSelectedGroups([...videoSelectedGroups, group.id]);
                                } else {
                                  setVideoSelectedGroups(
                                    videoSelectedGroups.filter((id) => id !== group.id)
                                  );
                                }
                              }}
                              style={{ color: '#5aa5ef' }}
                              size="small"
                            />
                          )}
                          label={(
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Group style={{ fontSize: 14, marginRight: 6, color: '#5aa5ef' }} />
                              <span
                                style={{
                                  fontFamily: 'Montserrat',
                                  fontSize: 12,
                                  color: '#1f2937',
                                  fontWeight: 500,
                                }}
                              >
                                {group.title}
                              </span>
                            </div>
                          )}
                          style={{
                            margin: '0',
                            padding: '4px 6px',
                            width: '100%',
                            borderRadius: '6px',
                          }}
                        />
                      ))
                    )}
                  </div>
                  {videoSelectedGroups.length === 0 && groupsLoaded && groups.length > 0 && (
                    <span
                      style={{
                        color: '#EF4444',
                        fontSize: 11,
                        fontFamily: 'Montserrat',
                        marginTop: 4,
                        display: 'block',
                      }}
                    >
                      Please select at least one group
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Album Settings Section */}
          {hasAlbums && (
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: 20,
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={sectionTitleStyle}>
                <PhotoLibrary style={{ fontSize: 20, color: '#10B981' }} />
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#1f2937',
                    fontFamily: 'Montserrat',
                  }}
                >
                  Album Settings
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: '#9CA3AF',
                    fontFamily: 'Montserrat',
                    marginLeft: 'auto',
                  }}
                >
                  {`${albumItems.length} album${albumItems.length !== 1 ? 's' : ''}`}
                </span>
              </div>

              {/* Visibility Dropdown */}
              <div style={{ maxWidth: '50%' }}>
                <label style={labelStyle}>Visibility</label>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={albumVisibility}
                    onChange={(e) => setAlbumVisibility(e.target.value)}
                    style={selectStyle}
                  >
                    <MenuItem value="2">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Public
                          style={{
                            fontSize: 16,
                            marginRight: 8,
                            color: '#10B981',
                          }}
                        />
                        <span
                          style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                        >
                          All Viewers
                        </span>
                      </div>
                    </MenuItem>
                    <MenuItem value="1">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Lock
                          style={{
                            fontSize: 16,
                            marginRight: 8,
                            color: '#EF4444',
                          }}
                        />
                        <span
                          style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                        >
                          Private
                        </span>
                      </div>
                    </MenuItem>
                    <MenuItem value="3">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Group
                          style={{
                            fontSize: 16,
                            marginRight: 8,
                            color: '#5aa5ef',
                          }}
                        />
                        <span
                          style={{ fontSize: 13, fontFamily: 'Montserrat' }}
                        >
                          Group
                        </span>
                      </div>
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Group Selection for Albums (when visibility is Group) */}
              {albumVisibility === '3' && (
                <div style={{ marginTop: 16 }}>
                  <label style={labelStyle}>Select Groups</label>
                  <div
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: 10,
                      padding: '8px',
                      maxHeight: '140px',
                      overflowY: 'auto',
                      border: albumSelectedGroups.length === 0
                        ? '2px solid #EF4444'
                        : '2px solid #e5e7eb',
                      boxShadow: albumSelectedGroups.length === 0
                        ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                        : 'none',
                    }}
                  >
                    {!groupsLoaded ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '50px',
                        }}
                      >
                        <CircularProgress size={16} style={{ color: '#5aa5ef' }} />
                        <span
                          style={{
                            marginLeft: '8px',
                            color: '#6B7280',
                            fontSize: '13px',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          Loading groups...
                        </span>
                      </div>
                    ) : groups.length === 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '50px',
                          backgroundColor: '#FEF2F2',
                          borderRadius: '8px',
                        }}
                      >
                        <span
                          style={{
                            color: '#9CA3AF',
                            fontStyle: 'italic',
                            fontSize: '13px',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          No groups available
                        </span>
                      </div>
                    ) : (
                      groups.map((group) => (
                        <FormControlLabel
                          key={group.id}
                          control={(
                            <Checkbox
                              checked={albumSelectedGroups.includes(group.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAlbumSelectedGroups([...albumSelectedGroups, group.id]);
                                } else {
                                  setAlbumSelectedGroups(
                                    albumSelectedGroups.filter((id) => id !== group.id)
                                  );
                                }
                              }}
                              style={{ color: '#5aa5ef' }}
                              size="small"
                            />
                          )}
                          label={(
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Group style={{ fontSize: 14, marginRight: 6, color: '#5aa5ef' }} />
                              <span
                                style={{
                                  fontFamily: 'Montserrat',
                                  fontSize: 12,
                                  color: '#1f2937',
                                  fontWeight: 500,
                                }}
                              >
                                {group.title}
                              </span>
                            </div>
                          )}
                          style={{
                            margin: '0',
                            padding: '4px 6px',
                            width: '100%',
                            borderRadius: '6px',
                          }}
                        />
                      ))
                    )}
                  </div>
                  {albumSelectedGroups.length === 0 && groupsLoaded && groups.length > 0 && (
                    <span
                      style={{
                        color: '#EF4444',
                        fontSize: 11,
                        fontFamily: 'Montserrat',
                        marginTop: 4,
                        display: 'block',
                      }}
                    >
                      Please select at least one group
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            background: '#ffffff',
          }}
        >
          <Button
            onClick={() => setMode('advanced')}
            variant="text"
            disabled={isPublishing}
            style={{
              color: '#5aa5ef',
              textTransform: 'none',
              fontWeight: 600,
              padding: '10px 16px',
              borderRadius: 8,
              fontFamily: 'Montserrat',
              fontSize: 13,
            }}
          >
            Advanced
          </Button>

          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={isPublishing}
              style={{
                color: '#666',
                borderColor: '#d0d0d0',
                textTransform: 'none',
                fontWeight: 500,
                padding: '10px 24px',
                borderRadius: 8,
                fontFamily: 'Montserrat',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              variant="contained"
              disabled={isPublishing}
              style={{
                background: isPublishing ? '#ccc' : '#5aa5ef',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                padding: '10px 32px',
                borderRadius: 8,
                boxShadow: isPublishing
                  ? 'none'
                  : '0 2px 8px rgba(90, 165, 239, 0.25)',
                fontFamily: 'Montserrat',
              }}
            >
              {isPublishing ? (
                <>
                  <CircularProgress
                    size={16}
                    style={{ color: '#fff', marginRight: 8 }}
                  />
                  Publishing...
                </>
              ) : (
                `Publish ${selectedItems.length} Item${selectedItems.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Advanced Mode Dialog */}
      <Dialog
        open={open && mode === 'advanced'}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            background: '#ffffff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconButton
              size="small"
              onClick={() => setMode('simple')}
              style={{ color: '#5aa5ef', marginRight: 4 }}
            >
              <ArrowBack style={{ fontSize: 20 }} />
            </IconButton>
            <h2
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                color: '#1a1a1a',
                fontFamily: 'Montserrat',
              }}
            >
              Advanced Settings
            </h2>
          </div>
          <IconButton
            onClick={onClose}
            size="small"
            style={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          TabIndicatorProps={{
            style: { backgroundColor: '#5aa5ef' },
          }}
          style={{
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}
        >
          <Tab
            label={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: activeTab === 0 ? '#5aa5ef' : '#6B7280' }}>
                <VideoLibrary style={{ fontSize: 18 }} />
                <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 13 }}>
                  {`Videos (${videos.length})`}
                </span>
              </div>
            )}
            disabled={!hasVideos}
            style={{ textTransform: 'none' }}
          />
          <Tab
            label={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: activeTab === 1 ? '#5aa5ef' : '#6B7280' }}>
                <PhotoLibrary style={{ fontSize: 18 }} />
                <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 13 }}>
                  {`Albums (${albums.length})`}
                </span>
              </div>
            )}
            disabled={!hasAlbums}
            style={{ textTransform: 'none' }}
          />
        </Tabs>

        {/* Content */}
        <DialogContent
          style={{
            padding: '24px',
            backgroundColor: '#fafafa',
            minHeight: 350,
            maxHeight: 450,
            overflowY: 'auto',
          }}
        >
          {/* Videos Tab */}
          {activeTab === 0 && (
            <div>
              {videos.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 40,
                    color: '#6B7280',
                  }}
                >
                  <VideoLibrary style={{ fontSize: 48, color: '#D1D5DB', marginBottom: 16 }} />
                  <p style={{ fontFamily: 'Montserrat', margin: 0 }}>No videos to edit</p>
                </div>
              ) : (
                videos.map((video) => renderAdvancedItemRow(video, true))
              )}
            </div>
          )}

          {/* Albums Tab */}
          {activeTab === 1 && (
            <div>
              {albums.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 40,
                    color: '#6B7280',
                  }}
                >
                  <PhotoLibrary style={{ fontSize: 48, color: '#D1D5DB', marginBottom: 16 }} />
                  <p style={{ fontFamily: 'Montserrat', margin: 0 }}>No albums to edit</p>
                </div>
              ) : (
                albums.map((album) => renderAdvancedItemRow(album, false))
              )}
            </div>
          )}
        </DialogContent>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 24px',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            background: '#ffffff',
          }}
        >
          <Button
            onClick={() => setMode('simple')}
            variant="text"
            disabled={isPublishing}
            style={{
              color: '#5aa5ef',
              textTransform: 'none',
              fontWeight: 600,
              padding: '10px 16px',
              borderRadius: 8,
              fontFamily: 'Montserrat',
              fontSize: 13,
            }}
          >
            Simple Mode
          </Button>

          {/* Quick Tools */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              backgroundColor: '#f3f4f6',
              padding: '4px 8px',
              borderRadius: 8,
            }}
          >
            <Tooltip title="Clear all titles">
              <IconButton
                size="small"
                onClick={() => handleClearAllTitles(activeTab === 0 ? 'video' : 'album')}
                style={{ color: '#6B7280', padding: 6 }}
              >
                <TitleIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear all descriptions">
              <IconButton
                size="small"
                onClick={() => handleClearAllDescriptions(activeTab === 0 ? 'video' : 'album')}
                style={{ color: '#6B7280', padding: 6 }}
              >
                <DescriptionIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {activeTab === 0 && (
              <Tooltip title="Apply same category to all videos">
                <IconButton
                  size="small"
                  onClick={() => setApplyCategoryDialogOpen(true)}
                  style={{ color: '#6B7280', padding: 6 }}
                >
                  <Category style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Apply same visibility to all">
              <IconButton
                size="small"
                onClick={() => setApplyVisibilityDialogOpen(true)}
                style={{ color: '#6B7280', padding: 6 }}
              >
                <VisibilityIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset all to original">
              <IconButton
                size="small"
                onClick={handleResetAll}
                style={{ color: '#6B7280', padding: 6 }}
              >
                <RefreshIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={isPublishing}
              style={{
                color: '#666',
                borderColor: '#d0d0d0',
                textTransform: 'none',
                fontWeight: 500,
                padding: '10px 24px',
                borderRadius: 8,
                fontFamily: 'Montserrat',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              variant="contained"
              disabled={isPublishing}
              style={{
                background: isPublishing ? '#ccc' : '#5aa5ef',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                padding: '10px 32px',
                borderRadius: 8,
                boxShadow: isPublishing
                  ? 'none'
                  : '0 2px 8px rgba(90, 165, 239, 0.25)',
                fontFamily: 'Montserrat',
              }}
            >
              {isPublishing ? (
                <>
                  <CircularProgress
                    size={16}
                    style={{ color: '#fff', marginRight: 8 }}
                  />
                  Publishing...
                </>
              ) : (
                `Publish ${selectedItems.length} Item${selectedItems.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Create Category Modal */}
      {showCreateCategoryModal && (
        <CreateCategoryPage
          initialOpen={showCreateCategoryModal}
          onModalClose={() => setShowCreateCategoryModal(false)}
          onCategoryCreated={handleCategoryCreated}
        />
      )}

      {/* Apply Category Dialog */}
      <Dialog
        open={applyCategoryDialogOpen}
        onClose={() => setApplyCategoryDialogOpen(false)}
        PaperProps={{
          style: { fontFamily: 'Montserrat' },
        }}
      >
        <DialogTitle style={{ fontFamily: 'Montserrat', fontWeight: 600 }}>
          Apply Category to All Videos
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <Select
              value={selectedCategoryForAll}
              onChange={(e) => {
                if (e.target.value === '__create_new__') {
                  setPendingItemIdForCategory('__all__');
                  setShowCreateCategoryModal(true);
                  setApplyCategoryDialogOpen(false);
                  return;
                }
                setSelectedCategoryForAll(e.target.value);
              }}
              displayEmpty
              style={{ fontFamily: 'Montserrat' }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    fontFamily: 'Montserrat',
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
                autoFocus: false,
                disableAutoFocusItem: true,
              }}
            >
              <MenuItem value="__create_new__">
                <div style={{ display: 'flex', alignItems: 'center', color: '#5aa5ef', fontFamily: 'Montserrat' }}>
                  <Add
                    style={{ fontSize: 16, marginRight: 8, color: '#5aa5ef' }}
                  />
                  <span style={{ fontWeight: 600 }}>Create New Category</span>
                </div>
              </MenuItem>
              <Divider style={{ margin: '4px 0' }} />
              <MenuItem value="" disabled>
                <em style={{ fontFamily: 'Montserrat' }}>Select a category</em>
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Montserrat' }}>
                    <Category
                      style={{ fontSize: 16, marginRight: 8, color: '#6B7280' }}
                    />
                    {cat.title}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApplyCategoryDialogOpen(false);
              setSelectedCategoryForAll('');
            }}
            style={{ fontFamily: 'Montserrat', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyCategoryToAll}
            variant="contained"
            disabled={!selectedCategoryForAll}
            style={{ backgroundColor: '#5aa5ef', fontFamily: 'Montserrat', textTransform: 'none' }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Visibility Dialog */}
      <Dialog
        open={applyVisibilityDialogOpen}
        onClose={() => setApplyVisibilityDialogOpen(false)}
        PaperProps={{
          style: { fontFamily: 'Montserrat' },
        }}
      >
        <DialogTitle style={{ fontFamily: 'Montserrat', fontWeight: 600 }}>
          {`Apply Visibility to All ${activeTab === 0 ? 'Videos' : 'Albums'}`}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <Select
              value={selectedVisibilityForAll}
              onChange={(e) => setSelectedVisibilityForAll(e.target.value)}
              style={{ fontFamily: 'Montserrat' }}
            >
              <MenuItem value="2">
                <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Montserrat' }}>
                  <Public
                    style={{ fontSize: 16, marginRight: 8, color: '#10B981' }}
                  />
                  All Viewers
                </div>
              </MenuItem>
              <MenuItem value="1">
                <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Montserrat' }}>
                  <Lock
                    style={{ fontSize: 16, marginRight: 8, color: '#EF4444' }}
                  />
                  Private
                </div>
              </MenuItem>
              <MenuItem value="3">
                <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Montserrat' }}>
                  <Group
                    style={{ fontSize: 16, marginRight: 8, color: '#8B5CF6' }}
                  />
                  Group
                </div>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApplyVisibilityDialogOpen(false)}
            style={{ fontFamily: 'Montserrat', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyVisibilityToAll}
            variant="contained"
            style={{ backgroundColor: '#5aa5ef', fontFamily: 'Montserrat', textTransform: 'none' }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PublishSettingsModal;
